import React from "react";
import { IMG, ACTIVITIES } from "../lib/constants";
import { UtensilsCrossed, Hammer, Wifi, Flame } from "lucide-react";

/**
 * SplitSection — reusable image-on-one-side / words-on-other-side layout
 * Used for Dining / Food / Bottles / Heritage / Activities
 */
function SplitSection({
    id,
    bg = "bg-cream",
    text = "text-coffee-900",
    accent = "text-forest-700",
    overline,
    title,
    titleAccent,
    body,
    image,
    imageAlt,
    children,
    reverse = false,
    extraImage,
    extraImageAlt,
    testid,
}) {
    return (
        <section id={id} className={`relative py-20 md:py-28 ${bg} ${text}`} data-testid={testid}>
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                    <p className={`text-xs tracking-[0.3em] uppercase ${accent} mb-5`}>{overline}</p>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] mb-6">
                        {title}
                        {titleAccent && (
                            <>
                                <br />
                                <span className={`italic ${accent}`}>{titleAccent}</span>
                            </>
                        )}
                    </h2>
                    <div className="space-y-4 text-base md:text-lg leading-relaxed opacity-85">
                        {body}
                    </div>
                    {children}
                </div>

                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""} grid ${extraImage ? "grid-cols-2 gap-4 md:gap-5" : "grid-cols-1"} h-[420px] md:h-[560px]`}>
                    <div className="relative overflow-hidden rounded-2xl group">
                        <img src={image} alt={imageAlt} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]" />
                    </div>
                    {extraImage && (
                        <div className="relative overflow-hidden rounded-2xl group">
                            <img src={extraImage} alt={extraImageAlt} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]" />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

/* 1. Coffee-Wood Dining Hall */
export function DiningHall() {
    return (
        <SplitSection
            id="dining"
            testid="dining-section"
            bg="bg-coffee-900"
            text="text-cream"
            accent="text-[#E8B769]"
            overline="Signature Dining"
            title="The Coffee-Wood"
            titleAccent="Dining Hall."
            image={IMG.diningNew1}
            imageAlt="Veeras coffee-wood dining hall by night"
            extraImage={IMG.diningNew2}
            extraImageAlt="Coffee-wood dining hall — night view"
            body={
                <>
                    <p>
                        Hand-built from <span className="text-[#E8B769] font-medium">pure Malnad coffee wood</span> —
                        every table is a slab from the estate's own century-old coffee trees.
                        Each grain is a quiet record of monsoons, mist and harvests.
                    </p>
                    <p>
                        Surrounded by hanging cart-wheels, fairy-lit ceilings and the gentle clatter
                        of dishes, every meal here feels less like dinner and more like an heirloom
                        you sit inside.
                    </p>
                </>
            }
        >
            <div className="mt-7 inline-flex items-center gap-2 text-xs md:text-sm text-cream/70 border border-cream/20 rounded-full px-4 py-2" data-testid="dining-fork-note">
                <UtensilsCrossed className="h-3.5 w-3.5 text-[#E8B769]" />
                <span>Eat the Malnad way — <span className="text-cream/85 font-medium">strictly no pork served.</span></span>
            </div>
        </SplitSection>
    );
}

/* 2. Authentic Malnad Food */
export function MalnadFood() {
    const seasonalFoods = [
        "Wild bamboo shoot curry",
        "Wild mushrooms",
        "Raw jackfruit curry",
        "Colocasia (taro) leaves",
        "Local fresh-water fish",
    ];
    const seasonalFruits = [
        "Mango", "Passion fruit", "Butter fruit", "Orange", "Guava",
        "Jackfruit", "Papaya", "Chikoo", "Pineapple", "Jamun",
        "Banana", "Rose apple", "Goan wild fruit", "Cashew fruit",
    ];
    return (
        <SplitSection
            id="food"
            testid="food-section"
            bg="bg-cream"
            text="text-coffee-900"
            accent="text-forest-700"
            overline="From the kitchen"
            title="Authentic"
            titleAccent="Malnad cuisine."
            image={IMG.veerasFood}
            imageAlt="A spread of authentic Malnad food at Veeras"
            extraImage={IMG.guestsFood}
            extraImageAlt="Happy guests at the Malnad breakfast table"
            reverse
            body={
                <>
                    <p>
                        Every dish at Veeras carries the memory of these hills — rice from
                        nearby paddies, jaggery from the village mill, freshly ground spices,
                        and forest-foraged greens cooked the slow Malnad way. Vegetarian and
                        non-vegetarian, both equally honoured.
                    </p>
                </>
            }
        >
            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="food-lists">
                <div className="p-5 bg-white border border-sand rounded-xl">
                    <p className="text-[11px] tracking-[0.25em] uppercase text-forest-700 mb-3">Seasonal specials</p>
                    <ul className="space-y-1.5 text-coffee-800 text-sm leading-relaxed">
                        {seasonalFoods.map((f) => (
                            <li key={f} className="flex items-start gap-2">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-forest-700 shrink-0" /> {f}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="p-5 bg-white border border-sand rounded-xl">
                    <p className="text-[11px] tracking-[0.25em] uppercase text-forest-700 mb-3">Seasonal fruits</p>
                    <div className="flex flex-wrap gap-1.5">
                        {seasonalFruits.map((f) => (
                            <span key={f} className="text-[12px] px-2.5 py-1 bg-forest-700/8 text-forest-700 border border-forest-700/15 rounded-full">{f}</span>
                        ))}
                    </div>
                </div>
            </div>
            <p className="mt-5 text-coffee-600 italic text-sm">
                Full menu coming soon — for now, simply trust our hands and arrive hungry.
            </p>
        </SplitSection>
    );
}

/* 3. Bottle Gallery */
export function BottleGallery() {
    return (
        <SplitSection
            id="bottles"
            testid="bottles-section"
            bg="bg-coffee-900"
            text="text-cream"
            accent="text-[#E8B769]"
            overline="A wall of colour"
            title="The famous"
            titleAccent="bottle gallery."
            image={IMG.bottleNew}
            imageAlt="Veeras bottle gallery wide view"
            extraImage={IMG.bottlesDay}
            extraImageAlt="Coloured bottle vases by day"
            body={
                <>
                    <p>
                        Hundreds of hand-painted glass bottles — collected, cleaned and
                        stained over years — hang in a quiet, glowing curtain across one
                        end of the property. Crystal vases below cradle ferns and wildflowers
                        from the estate.
                    </p>
                    <p>
                        A small piece of art in motion: the same wall looks like a watercolour
                        by day and like a stained-glass cathedral by night.
                    </p>
                </>
            }
        />
    );
}

/* 4. Living Museum (Heritage) — placed AFTER dining/food */
export function LivingMuseum() {
    return (
        <SplitSection
            id="museum"
            testid="museum-section"
            bg="bg-cream"
            text="text-coffee-900"
            accent="text-forest-700"
            overline="Living museum"
            title="150 years of Malnad,"
            titleAccent="antique &amp; vintage — around every meal."
            image={IMG.malnadAntiqueWall}
            imageAlt="Veeras antique and vintage Malnad heritage wall"
            reverse
            body={
                <>
                    <p>
                        A walk through Veeras is a walk through <span className="text-forest-700 font-medium">150 years of Malnad antique &amp; vintage collection</span> —
                        original ploughs, wooden pestles, brass lanterns, bullock yokes, hand-painted toys,
                        guitars, lanterns and cart-wheels that worked, played and lit these very hills over
                        a century and a half ago. Untouched. Unrestored. On quiet display.
                    </p>
                    <p>
                        Our guests sit beneath them every meal — savouring breakfasts, lunches, dinners
                        and lazy evening cups of coffee, while a piece of history watches gently from the walls.
                    </p>
                </>
            }
        >
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-coffee-700 border border-coffee-300 rounded-full px-4 py-2">
                <Hammer className="h-3.5 w-3.5 text-forest-700" />
                <span>Some pieces are older than the trees outside.</span>
            </div>
        </SplitSection>
    );
}

/* 5. Activities */
export function ActivitiesSection() {
    return (
        <section id="features" className="relative py-20 md:py-28 bg-coffee-900 text-cream" data-testid="features-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <div className="lg:col-span-5">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#E8B769] mb-5">Indoor &amp; outdoor</p>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] mb-6">
                        Eleven ways to <br />
                        <span className="italic text-[#E8B769]">forget your phone.</span>
                    </h2>
                    <div className="space-y-4 text-cream/85 text-base md:text-lg leading-relaxed">
                        <p>
                            From the splash of the swimming pool and a friendly round of slush
                            volleyball, to a quiet evening of carrom and chess — Veeras is built
                            to play. Our outdoor zip-line and commando net keep the brave busy,
                            while foosball, snooker and shuttle look after the rest.
                        </p>
                        <p>
                            Whether you arrive with kids, a cricket team, or someone who hasn't
                            laughed in a while — there's a corner here that will look after them.
                        </p>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <div className="relative overflow-hidden rounded-3xl h-[260px] md:h-[340px] mb-5">
                        <img src={IMG.gamesAll} alt="Activities at Veeras" className="absolute inset-0 h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-coffee-900 via-coffee-900/30 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-cream/85">
                            <span className="text-xs tracking-[0.3em] uppercase">Activities</span>
                            <span className="font-serif text-3xl md:text-4xl">11</span>
                        </div>
                    </div>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3" data-testid="activities-list">
                        {ACTIVITIES.map((a) => (
                            <li key={a} className="flex items-center gap-2.5 text-cream/90 text-sm md:text-base" data-testid={`activity-${a.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-[#E8B769] shrink-0" />
                                <span>{a}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-5 text-cream/45 text-xs tracking-wide" data-testid="activities-note">
                        * Snooker / Pool Board — conditions apply, learners not allowed.
                    </p>

                    {/* Amenity strip */}
                    <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { icon: Wifi, label: "Free Wi-Fi" },
                            { icon: Flame, label: "24-hr hot water" },
                            { icon: UtensilsCrossed, label: "Pure Malnad cuisine" },
                            { icon: Hammer, label: "150-yr heritage" },
                        ].map((a) => (
                            <div key={a.label} className="flex items-center gap-3 px-3 py-3 bg-white/[0.04] border border-cream/10 rounded-xl">
                                <a.icon className="h-4 w-4 text-[#E8B769] shrink-0" />
                                <span className="text-cream/85 text-sm">{a.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* 6. Car & Bike rent strip */
export function RentStrip() {
    return (
        <section className="relative py-14 md:py-20 bg-cream" data-testid="rent-section">
            <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
                <div className="md:col-span-6">
                    <p className="text-xs tracking-[0.3em] uppercase text-forest-700 mb-3">Getting around</p>
                    <h3 className="font-serif text-3xl md:text-4xl text-coffee-900 font-light leading-tight">
                        Car &amp; bike rentals <span className="italic text-forest-700">on request.</span>
                    </h3>
                    <p className="mt-4 text-stone-600 text-sm md:text-base leading-relaxed">
                        Explore Sakleshpur, the Western Ghats and nearby viewpoints at your own pace —
                        we'll arrange the wheels for you.
                    </p>
                </div>
                <div className="md:col-span-6 grid grid-cols-2 gap-3">
                    <div className="relative h-28 md:h-32 rounded-xl overflow-hidden">
                        <img src={IMG.carRent} alt="Car rental" className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                    <div className="relative h-28 md:h-32 rounded-xl overflow-hidden">
                        <img src={IMG.bikeRent} alt="Bike rental" className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                </div>
            </div>
        </section>
    );
}
